import { useState, useEffect, ReactNode, useMemo } from "react";
import { useLang } from "../../contexts/LangContext";
import { useNavigate } from "react-router-dom";
import { fishDatabase, FishSpecies } from "../../data/fish_db";
import { commonOtherFish, CommonOtherFish } from "../../data/common_other_fish";
import { useLogSession } from "../../contexts/LogSessionContext";
import { v4 as uuidv4 } from "uuid";
import FishCard from "../../cards/FishCard";
import FishDetailsCard from "../../cards/FishDetailsCard";
import ModalWindow from "../../components/ModalWindow";

export default function WhichFish() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { currentMealRecord, setcurrentMealRecord, setNewFishItem, person } =
    useLogSession();
  const [searchText, setSearchText] = useState<string>("");
  const [filteredFishes, setFilteredFishes] =
    useState<FishSpecies[]>(fishDatabase);
  
  const [modalContent, setModalContent] = useState<ReactNode>("");

  // "Other" fish name capture (modal)
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [otherFishName, setOtherFishName] = useState("");

  // Get fish popularity based on meal logs - most recent first
  const fishPopularity = useMemo(() => {
    const popularity = new Map<string, { lastUsed: number; count: number }>();
    
    // Iterate through all day records and meals to count fish usage
    person.records.forEach((dayRecord) => {
      const dayTimestamp = new Date(dayRecord.date).getTime();
      dayRecord.meals.forEach((meal) => {
        meal.fish_items.forEach((item) => {
          const existing = popularity.get(item.fish_id);
          if (!existing || dayTimestamp > existing.lastUsed) {
            popularity.set(item.fish_id, {
              lastUsed: dayTimestamp,
              count: (existing?.count || 0) + 1
            });
          } else {
            popularity.set(item.fish_id, {
              ...existing,
              count: existing.count + 1
            });
          }
        });
      });
    });
    
    return popularity;
  }, [person.records]);

  // Sort fish by popularity (most recent first, then by count)
  const sortedFishDatabase = useMemo(() => {
    return [...fishDatabase].sort((a, b) => {
      const aPopularity = fishPopularity.get(a.id);
      const bPopularity = fishPopularity.get(b.id);
      
      // If only one has history, it goes first
      if (aPopularity && !bPopularity) return -1;
      if (!aPopularity && bPopularity) return 1;
      
      // If both have history, sort by most recent first
      if (aPopularity && bPopularity) {
        if (aPopularity.lastUsed !== bPopularity.lastUsed) {
          return bPopularity.lastUsed - aPopularity.lastUsed;
        }
        // If same timestamp, sort by count
        return bPopularity.count - aPopularity.count;
      }
      
      // Neither has history, keep original order
      return 0;
    });
  }, [fishPopularity]);

  useEffect(() => {
    const searchLower = searchText.toLowerCase().trim();
    if (searchLower) {
      setFilteredFishes(
        sortedFishDatabase.filter(
          (fish) =>
            fish.english_name.toLowerCase().includes(searchLower) ||
            fish.local_name.toLowerCase().includes(searchLower) ||
            fish.aliases?.some((alias) =>
              alias.toLowerCase().includes(searchLower)
            )
        )
      );
    } else {
      setFilteredFishes(sortedFishDatabase);
    }
  }, [searchText, sortedFishDatabase]);

  // Determine whether to show the "Other" fish card (always when search is
  // empty, or when the search text matches "other" / "অন্যান্য")
  const showOtherCard = useMemo(() => {
    const searchLower = searchText.toLowerCase().trim();
    if (!searchLower) return true;
    return (
      "other".includes(searchLower) ||
      "অন্যান্য".includes(searchLower) ||
      searchLower.includes("other") ||
      searchLower.includes("অন্যান্য")
    );
  }, [searchText]);

  // Live-sort the "Other" quick-pick list: entries matching the typed text rise
  // to the top (exact/prefix first), while non-matching entries stay visible.
  const sortedOtherFish = useMemo(() => {
    const query = otherFishName.trim().toLowerCase();
    if (!query) return commonOtherFish;

    const score = (fish: CommonOtherFish): number => {
      const en = fish.english_name.toLowerCase();
      const bn = fish.local_name.toLowerCase();
      const id = fish.id.toLowerCase();
      if (en === query || bn === query || id === query) return 0;
      if (en.startsWith(query) || bn.startsWith(query) || id.startsWith(query)) return 1;
      if (en.includes(query) || bn.includes(query) || id.includes(query)) return 2;
      return 3;
    };

    return [...commonOtherFish].sort((a, b) => score(a) - score(b));
  }, [otherFishName]);

  const handleSelect = (
    fish_id: string,
    english_name: string,
    local_name: string,
    fishNameOverride?: string
  ) => {
    if (!currentMealRecord) return;

    const fish_name = fishNameOverride ?? `${english_name} (${local_name})`;

    setNewFishItem({
      id: uuidv4(),
      fish_id,
      fish_name,
      fish_english_name: english_name,
      fish_local_name: local_name,
      recipe: "unknown",
      portion_type: "portion",
      piece_type: undefined,
      number_of_portions: 1,
      weight_per_portion: 0,
      estimated_weight: 0,
      weight_unit: "gm",
      consumed_soft_bone: false,
      ingredients: "",
    });
    navigate("../which-recipe");
  };

  const openOtherModal = () => {
    setOtherFishName("");
    setShowOtherModal(true);
  };

  const confirmOtherFish = () => {
    const name = otherFishName.trim();
    if (name) {
      handleSelect("other", name, name, name);
    } else {
      handleSelect("other", "Other", "অন্যান্য");
    }
    setShowOtherModal(false);
  };

  const selectCommonOtherFish = (english_name: string, local_name: string) => {
    handleSelect("other", english_name, local_name);
    setShowOtherModal(false);
  };

  const handleLongPress = (fish: FishSpecies) => {
    if (!fish) return;
    setModalContent(
      <div className="mx-scr">
        <FishDetailsCard fish={fish} />
      </div>
    );
  };

  return (
    <div className="w-full grow flex flex-col items-center justify-start text-center px-scr pt-8">
      <h1 className="text-2xl font-bold text-rose-700 mb-6">
        {t("whichFish_Heading")}
      </h1>

      <div className="w-full flex items-center mb-4 ">
        <input
          type="text"
          placeholder={t("whichFish_SearchPlaceholder")}
          className="w-full !h-12 input-base  !bg-gray-100 !border !text-base !border-gray-300"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
        {searchText && (
          <button
            className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-300 text-gray-600 -ml-10"
            onClick={() => setSearchText("")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {filteredFishes.length === 0 && !showOtherCard && (
        <p className="text-gray-500">{t("whichFish_NoResults")}</p>
      )}
      {filteredFishes.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          {filteredFishes.map((fish) => (
            <FishCard
              key={fish.id}
              fish={fish}
              onSelect={() => handleSelect(fish.id, fish.english_name, fish.local_name) }
              onLongPress={() => { handleLongPress(fish); }}
            />
          ))}
        </div>
      )}

      {showOtherCard && (
        <div
          className="w-full mb-8 flex flex-col items-center justify-center rounded-xl p-4 shadow-sm bg-gray-200 bg-opacity-75 backdrop-blur-md hover:shadow-lg transition duration-200 cursor-pointer"
          onClick={openOtherModal}
        >
          <svg
            viewBox="0 0 512 512"
            className="w-32 h-32 text-gray-500"
            xmlSpace="preserve"
          >
            <g>
              <path
                fill="currentColor"
                d="M120.796,169.319c-57.625,18.219-101.25,51.094-120.797,79.813c19.719,29,63.953,62.172,122.328,80.297 c14.656-22.734,23.172-49.766,23.172-78.844C145.499,220.475,136.405,192.538,120.796,169.319z M78.499,252.725 c-8.328,0-15.063-6.75-15.063-15.063c0-8.344,6.734-15.078,15.063-15.078s15.078,6.734,15.078,15.078 C93.577,245.975,86.827,252.725,78.499,252.725z"
              />
              <path
                fill="currentColor"
                d="M497.405,251.256c-1.047-3.234-1.047-6.734,0-9.984l13.813-42.891c2.031-6.313,0.031-13.219-5.047-17.484 c-5.094-4.25-12.25-4.984-18.094-1.875l-81.328,43.313c-40.094-29.016-80.078-47.234-118.563-57.234 c-0.609-7.703-2.688-17.563-8.688-25.078c-9.438-11.766-69.125,5.453-98.172,16.766c-15.719,1.594-30.828,4.406-45.172,8.125 c15.594,24.828,24.656,54.203,24.656,85.672c0,30.406-8.438,58.875-23.094,83.172c21.297,5.406,44.234,8.766,68.344,9.219 c0.922,1.406,2.063,2.703,3.438,3.797l32.047,25.594c6.813,5.438,16.703,4.547,22.438-2.016l4.578-5.266 c3.406-3.906,4.75-9.219,3.594-14.281c-1.141-5.078-4.656-9.281-9.422-11.344l-1.328-0.578 c47.938-8.016,98.859-28.297,149.891-66.25l76.781,40.891c5.844,3.109,13,2.375,18.094-1.891c5.078-4.25,7.078-11.172,5.047-17.469 L497.405,251.256z"
              />
              <text
                x="250"
                y="300"
                textAnchor="middle"
                fontSize="150"
                fontWeight="700"
                fill="#ffffff"
              >
                ?
              </text>
            </g>
          </svg>
          <div className="mt-2 text-lg font-bold text-gray-800">
            {t("whichFish_Other")}
          </div>
        </div>
      )}

      {modalContent && (
        <ModalWindow
          isOpen={!!modalContent}
          onClose={() => setModalContent("")}        >
          {modalContent}
        </ModalWindow>
      )}

      {showOtherModal && (
        <ModalWindow
          isOpen={showOtherModal}
          onClose={() => setShowOtherModal(false)}
          modalTitle={t("whichFish_OtherFishNameTitle")}
        >
          <div className="mx-scr bg-white bg-opacity-75 backdrop-blur-md rounded-3xl shadow-lg p-4">
            <div className="flex flex-col gap-4">
              <input
                type="text"
                autoFocus
                value={otherFishName}
                onChange={(e) => setOtherFishName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmOtherFish();
                }}
                placeholder={t("whichFish_OtherFishNamePlaceholder")}
                className="w-full input-base !bg-white !text-base !text-left !px-4 !py-3 border-2 border-gray-200 focus:border-blue-500"
              />

              <div>
                <p className="text-sm font-semibold text-gray-700 my-2 text-left">
                  {t("whichFish_OtherFishCommonList")}
                </p>
                <div className="flex flex-wrap gap-2 py-2 max-h-80 overflow-y-auto">
                  {sortedOtherFish.map((fish) => (
                    <button
                      key={fish.id}
                      type="button"
                      onClick={() =>
                        selectCommonOtherFish(fish.english_name, fish.local_name)
                      }
                      className="px-3 py-2 rounded-full text-base font-semibold bg-white text-blue-700 border border-gray-300 hover:bg-blue-50 hover:border-blue-400"
                    >
                      {lang === "bn" ? fish.local_name : fish.english_name}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={confirmOtherFish}
                className="button-small button-bg-grad-2"
              >
                {t("whichFish_OtherFishConfirm")}
              </button>
            </div>
          </div>
        </ModalWindow>
      )}
    </div>
  );
}
